import React, { useEffect, useState } from 'react';
import { Grid, Header, Segment, Icon, Label } from 'semantic-ui-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFormSubmissionsClient } from '../submissions/formSubmissions.client';
import { useFluidFormConfigClient } from '../formBuilder/fluidFormConfig.client';
import { isWhatsAppSubmission, isSlackSubmission } from '../../shared';
import { formatDate } from '../date.format';
import SubmissionDataEditor from './SubmissionDataEditor';
import ValidationMessages from './ValidationMessages.component';
import AttachmentViewer from './AttachmentViewer.component';
import PageLayout from '../components/PageLayout';
import { renderStatusBadge } from '../components/StatusBadge';
import CollapsiblePanel from '../components/CollapsiblePanel';
import Loader from '../components/Loader';
const SubmissionDetailPage = () => {
    const { submissionId } = useParams();
    const navigate = useNavigate();
    const submissionsClient = useFormSubmissionsClient();
    const formConfigClient = useFluidFormConfigClient();
    const [submission, setSubmission] = useState(null);
    const [formConfig, setFormConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingReviewed, setUpdatingReviewed] = useState(false);
    const [clearingValidations, setClearingValidations] = useState(false);
    const [attachmentsPanelMinimized, setAttachmentsPanelMinimized] = useState(!submission?.attachments?.length);
    useEffect(() => {
        if (submissionId) {
            fetchSubmissionDetail();
        }
    }, [submissionId]);
    const fetchSubmissionDetail = async (useLoading = true) => {
        try {
            if (useLoading) {
                setLoading(true);
            }
            setError(null);
            const submissionData = await submissionsClient.getSubmission(submissionId);
            setSubmission(submissionData);
            // Update attachments panel state based on whether there are attachments
            setAttachmentsPanelMinimized(!submissionData.attachments?.length);
            // Load form config for field labels
            if (submissionData.formKey) {
                const formConfigData = await formConfigClient.getFormConfig(submissionData.formKey, submissionId);
                setFormConfig(formConfigData);
            }
        }
        catch (err) {
            console.error('Error fetching submission detail:', err);
            setError('Failed to load submission details. Please try again.');
        }
        finally {
            if (useLoading) {
                setLoading(false);
            }
        }
    };
    const handleUpdate = async () => {
        // @TODO: FLU-193 Handle this.
        const submissionData = await submissionsClient.getSubmission(submissionId);
        ///update errors
        // setSubmission({ ...submissionData, validationResults: submission.validationResults });
    };
    const handleReviewedChange = async (checked) => {
        if (!submission)
            return;
        setUpdatingReviewed(true);
        try {
            const newStatus = checked ? 'finalized' : 'review';
            await submissionsClient.updateSubmissionStatus(submission.submissionId, newStatus);
            setSubmission({ ...submission, status: newStatus });
        }
        catch (error) {
            setError('Failed to update reviewed status. Please try again.');
        }
        finally {
            setUpdatingReviewed(false);
        }
    };
    const handleClearValidations = async () => {
        if (!submission)
            return;
        setClearingValidations(true);
        try {
            await submissionsClient.clearValidationResults(submission.submissionId);
            // Refetch submission data to show updated validation results
            await fetchSubmissionDetail();
        }
        catch (error) {
            console.error('Error clearing validation results:', error);
            setError('Failed to clear validation results. Please try again.');
        }
        finally {
            setClearingValidations(false);
        }
    };
    return (React.createElement(PageLayout, { title: "Submission Details", subtitle: submission ? `Submitted on ${formatDate(submission.sentDate)}${submission.userName ? ` by ${submission.userName}` : ''}` : '', showBackButton: true, onBackClick: () => navigate('/submissions'), variant: "wide", error: error },
        loading && React.createElement(Loader, { text: "Loading submission details..." }),
        !loading && submission && formConfig && React.createElement(SubmissionDetailContent, { submission: submission, formConfig: formConfig, handleClearValidations: handleClearValidations, clearingValidations: clearingValidations, handleReviewedChange: handleReviewedChange, updatingReviewed: updatingReviewed, handleUpdate: handleUpdate, attachmentsPanelMinimized: attachmentsPanelMinimized, setAttachmentsPanelMinimized: setAttachmentsPanelMinimized })));
};
function SubmissionDetailContent({ submission, formConfig, handleClearValidations, clearingValidations, handleReviewedChange, updatingReviewed, handleUpdate, attachmentsPanelMinimized, setAttachmentsPanelMinimized }) {
    return React.createElement(Grid, null,
        React.createElement(Grid.Row, null,
            React.createElement(Grid.Column, { width: 16 },
                React.createElement(ValidationMessages, { validationResult: submission?.validationResults, onClear: handleClearValidations, clearingInProgress: clearingValidations }))),
        React.createElement(Grid.Row, null,
            React.createElement(Grid.Column, { width: 16 },
                React.createElement(Segment, null,
                    React.createElement(Grid, null,
                        React.createElement(Grid.Column, { width: 3 },
                            React.createElement(Header, { as: "h5" }, "Submission ID"),
                            React.createElement("code", { style: { fontSize: '0.9rem' } }, submission.submissionId)),
                        React.createElement(Grid.Column, { width: 3 },
                            React.createElement(Header, { as: "h5" }, "Form"),
                            React.createElement(Link, { to: `/form-config/${submission.formKey}` }, formConfig?.name || submission.formKey)),
                        React.createElement(Grid.Column, { width: 3 },
                            React.createElement(Header, { as: "h5" }, "User"),
                            submission.userName ? (React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
                                isSlackSubmission(submission) && React.createElement(Icon, { name: "slack", size: "small", color: "purple" }),
                                isWhatsAppSubmission(submission) && React.createElement(Icon, { name: "whatsapp", size: "small", color: "green" }),
                                submission.userName)) : (React.createElement("span", { style: { color: '#999' } }, "Unknown"))),
                        React.createElement(Grid.Column, { width: 3 },
                            React.createElement(Header, { as: "h5" }, "Attachments"),
                            React.createElement(Label, { color: submission.attachments?.length ? 'green' : 'grey' },
                                React.createElement(Icon, { name: "paperclip" }),
                                submission.attachments?.length || 0)),
                        React.createElement(Grid.Column, { width: 4 },
                            React.createElement(Header, { as: "h5" }, "Status"),
                            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
                                renderStatusBadge(submission.status || 'draft'),
                                submission.status !== 'finalized' ? (React.createElement("button", { onClick: () => handleReviewedChange(true), disabled: updatingReviewed, className: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" }, updatingReviewed ? (React.createElement(React.Fragment, null,
                                    React.createElement(Icon, { name: "spinner", loading: true, style: { marginRight: '4px' } }),
                                    "Updating...")) : ('Finalize Review'))) : (React.createElement("button", { onClick: () => handleReviewedChange(false), disabled: updatingReviewed, className: "inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" }, updatingReviewed ? (React.createElement(React.Fragment, null,
                                    React.createElement(Icon, { name: "spinner", loading: true, style: { marginRight: '4px' } }),
                                    "Updating...")) : ('Back to Review'))))))))),
        React.createElement(Grid.Row, null,
            !attachmentsPanelMinimized && (React.createElement(Grid.Column, { width: 8 },
                React.createElement(CollapsiblePanel, { title: `Attachments${submission.attachments?.length ? ` (${submission.attachments.length})` : ''}`, icon: "paperclip", isMinimized: false, onToggle: setAttachmentsPanelMinimized },
                    React.createElement(AttachmentViewer, { submission: submission })))),
            React.createElement(Grid.Column, { width: attachmentsPanelMinimized ? 16 : 8 },
                attachmentsPanelMinimized && (React.createElement("div", { style: { marginBottom: '1rem' } },
                    React.createElement(CollapsiblePanel, { title: `Attachments${submission.attachments?.length ? ` (${submission.attachments.length})` : ''}`, icon: "paperclip", isMinimized: true, onToggle: setAttachmentsPanelMinimized },
                        React.createElement(AttachmentViewer, { submission: submission })))),
                React.createElement(CollapsiblePanel, { title: "Submission Data", icon: "table", minimizable: false },
                    React.createElement(SubmissionDataEditor, { submissionInitial: submission, formConfigInitial: formConfig, onUpdate: handleUpdate })))));
}
export default SubmissionDetailPage;
